import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * TagsInput — chips input avec autocomplete sur les tags existants de la company.
 * - Enter/virgule pour valider un tag
 * - Backspace sur input vide retire le dernier tag
 * - Autocomplete case-insensitive
 * - Normalisation client (trim + dédup case-insensitive), la validation stricte
 *   (chars autorisés, ≤40, max 15) est refaite côté serveur.
 */
export function TagsInput({ value, onChange, placeholder = "Ajouter un tag…" }) {
  const { state } = useAuth();
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/leads/tags");
        if (!cancelled) setAllTags(data.items || []);
      } catch {
        /* silencieux */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  const lowerValue = value.map((t) => t.toLowerCase());

  useEffect(() => {
    if (!input.trim()) {
      setSuggestions([]);
      return;
    }
    const q = input.trim().toLowerCase();
    const filtered = allTags
      .filter((it) => it.tag.toLowerCase().includes(q))
      .filter((it) => !lowerValue.includes(it.tag.toLowerCase()))
      .slice(0, 8);
    setSuggestions(filtered);
  }, [input, allTags, value]);

  const addTag = (raw) => {
    const t = (raw || "").trim();
    if (!t) return;
    if (t.length > 40) return;
    if (value.length >= 15) return;
    if (lowerValue.includes(t.toLowerCase())) return;
    // Réutilise la casse existante si possible
    const canonical = allTags.find((x) => x.tag.toLowerCase() === t.toLowerCase());
    onChange([...value, canonical ? canonical.tag : t]);
    setInput("");
    setShowSuggestions(false);
  };

  const removeTag = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && value.length > 0) {
      removeTag(value.length - 1);
    }
  };

  return (
    <div className="relative" data-testid="tags-input">
      <div
        className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-700 bg-slate-950/40 p-2 focus-within:border-[#C4553B]"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="inline-flex items-center gap-1 rounded-full bg-[#C4553B]/15 border border-[#C4553B]/40 px-2 py-0.5 text-xs text-[#E9A98F]"
            data-testid={`tag-chip-${t}`}
          >
            {t}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(i);
              }}
              className="hover:text-white"
              aria-label={`Retirer ${t}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder={value.length === 0 ? placeholder : ""}
          maxLength={40}
          className="flex-1 min-w-[100px] bg-transparent px-1 py-0.5 text-sm text-slate-100 outline-none placeholder:text-slate-500"
          data-testid="tag-input-field"
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-700 bg-slate-900 shadow-lg">
          {suggestions.map((s) => (
            <li
              key={s.tag}
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s.tag);
              }}
              className="cursor-pointer px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
              data-testid={`tag-suggestion-${s.tag}`}
            >
              {s.tag} <span className="text-xs text-slate-500">({s.count})</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-[10px] text-slate-500">
        Entrée ou virgule pour valider · max 15 tags · 40 caractères max
      </p>
    </div>
  );
}
